import {
  BaseUserSchema,
  Device,
  Document,
  DocumentAnswerItemSchema,
  DocumentSchema,
  DocumentTemplate,
  DocumentVersionApproverSchema,
  Folder,
  FolderSchema,
  IntegrationResponseSchema,
  McpConnectionsResponseSchema,
  OrgScopedBaseUser,
  OrgScopedBaseUserSchema,
  SlackAuthUrlResponseSchema,
  SlackIntegrationResponseSchema,
  TraceabilityMatrix,
  TrashedDocument,
  TrashedDocumentSchema,
  UserSchema,
  UserSurveyAnswersSchema,
  UserTodosSchema,
  UserTodosSummarySchema,
  deviceSchema,
  documentTemplateSchemas,
  roadmapCardsSchema,
  roadmapStagesSchema,
  taskDefinitionsSchema,
  traceabilityMatrixSchema,
} from "@models";
import {
  keepPreviousData,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { JSONContent } from "@tiptap/core";
import { FunctionalGroup, IntegrationType } from "@types";
import {
  captureSentryException,
  queryClient as queryClientAsync,
} from "@utils";
import { PostHog, usePostHog } from "posthog-js/react";
import type { DynamicContentResolution } from "src/tiptap/components/tiptap-node/dynamic-nodes/dynamic-node-context";
import { transformTemplatesConfigToFormat } from "src/utils/documentTemplates";
import {
  normalizeNullableUserNameFields,
  setTracingContext,
} from "src/utils/user";
import {
  getAdminDevicesQueryKey,
  getAdminOrganizationsQueryKey,
  getAdminRoadmapCardsQueryKey,
  getAdminRoadmapStagesQueryKey,
  getAllFilesInOrgQueryKey,
  getAnswerItemsByTypeQueryKey,
  getAnswerItemsQueryKey,
  getApproversQueryKey,
  getDataKeyDefinitionsQueryKey,
  getDataKeyValueQueryKey,
  getDeviceQueryKey,
  getDevicesQueryKey,
  getDocumentQueryKey,
  getDocumentTemplatesQueryKey,
  getDynamicContentForKeysQueryKey,
  getFileQueryKey,
  getFoldersQueryKey,
  getFunctionalGroupsQueryKey,
  getGithubAuthUrlQueryKey,
  getIntegrationQueryKey,
  getJiraAuthUrlQueryKey,
  getLinearAuthUrlQueryKey,
  getMcpConnectionsQueryKey,
  getOrganizationUsersQueryKey,
  getRoadmapCardsQueryKey,
  getRoadmapStagesQueryKey,
  getRoadmapTasksQueryKey,
  getSlackAuthUrlQueryKey,
  getTraceabilityMatrixQueryKey,
  getUserByIdQueryKey,
  getUserQueryKey,
  getUserSurveyAnswersQueryKey,
} from "../config";
import {
  getDocumentTrashQueryKey,
  getDocumentsQueryKey,
  getFrameTemplateForDocumentQueryKey,
  getFrameTemplateQueryKey,
  getTasksQueryKey,
  getTemplateDynamicContentQueryKey,
  getTrashedDocumentQueryKey,
  getUserDataKeysQueryKey,
  getUserTodosQueryKey,
  getUserTodosSummaryQueryKey,
} from "../config/request-keys";
import {
  AdminDevice,
  AdminOrganization,
  getAdminDevices,
  getAdminOrganizations,
  getAdminRoadmapCards,
  getAdminRoadmapStages,
  getAllFilesInOrg,
  getAnswerItemsById,
  getAnswerItemsByTypeId,
  getDataKeyDefinitions,
  getDataKeyValue,
  getDataKeys,
  getDevice,
  getDevices,
  getDocument,
  getDocumentApprovers,
  getDocumentExplorerTemplates,
  getDocuments,
  getDocumentsTemplates,
  getDynamicContent,
  getFile,
  getFolders,
  getFrameTemplateById,
  getFrameTemplateByKey,
  getFunctionalGroups,
  getGithubAuthUrl,
  getIntegration,
  getJiraAuthUrl,
  getLinearAuthUrl,
  getMcpConnections,
  getRoadmapCards,
  getRoadmapStages,
  getRoadmapTasks,
  getSlackAuthUrl,
  getTemplateDynamicContent,
  getTraceabilityMatrix,
  getTrashedDocument,
  getUser,
  getUserById,
  getUserDataKeys,
  getUserSurveyAnswers,
  getUserTodos,
  getUserTodosSummary,
  getUsersByOrg,
} from "../services";
import { rootStore } from "../stores";
import {
  coalesceInFlightRequests,
  filterExplorerDocumentsForDevice,
  resolveExplorerTemplatesForDevice,
} from "./documentExplorerQueries";

const { deviceStore } = rootStore;

type UserContextDataKey = {
  key: string;
  label?: string;
};

const getUserParams = (enabled: boolean = true, postHog?: PostHog) => {
  return {
    queryKey: getUserQueryKey(),
    queryFn: async () => {
      const { data } = await getUser();
      const user = await UserSchema.validate(
        normalizeNullableUserNameFields(data)
      );
      // Important for logging and debugging
      setTracingContext(user, postHog);
      return user;
    },
    enabled,
  };
};

export function useGetUser(enabled: boolean = true) {
  //TODO: Fix posthog to work for both hooks/function
  // Also fix: Warning: [PostHog.js] was already loaded elsewhere even thoug it seems to be a common issue
  // https://github.com/PostHog/posthog-js/issues/723
  const postHog = usePostHog();

  return useQuery(getUserParams(enabled, postHog));
}

export function useGetUserDataKeys(enabled: boolean = true) {
  return useQuery<UserContextDataKey[]>({
    queryKey: getUserDataKeysQueryKey(),
    queryFn: async () => {
      const { data } = await getUserDataKeys();
      if (!Array.isArray(data)) {
        return [];
      }

      return data.flatMap((item): UserContextDataKey[] => {
        if (!item || typeof item !== "object") {
          return [];
        }

        const key =
          "key" in item && typeof item.key === "string" ? item.key : null;

        if (!key) {
          return [];
        }

        const question =
          "question" in item &&
          item.question &&
          typeof item.question === "object"
            ? item.question
            : null;
        const options =
          question &&
          "options" in question &&
          question.options &&
          typeof question.options === "object"
            ? question.options
            : null;
        const label =
          options && "label" in options && typeof options.label === "string"
            ? options.label
            : undefined;

        return [{ key, label }];
      });
    },
    enabled,
  });
}

export function getUserAsync(enabled: boolean = true) {
  return queryClientAsync.fetchQuery(getUserParams(enabled));
}

const getUserByIdParams = (userId: string) => {
  return {
    queryKey: getUserByIdQueryKey(userId),
    queryFn: async () => {
      const { data } = await getUserById(userId);
      return await BaseUserSchema.validate(
        normalizeNullableUserNameFields(data)
      );
    },
  };
};

export function useGetUserById({ userId }: { userId: string }) {
  return useQuery(getUserByIdParams(userId));
}

export function useGetUserTodos(
  orgId: string,
  deviceId?: string,
  enabled: boolean = false
) {
  return useQuery({
    queryKey: getUserTodosQueryKey(orgId, deviceId),
    queryFn: async () => {
      const { data } = await getUserTodos(orgId, deviceId);
      return await UserTodosSchema.validate(data);
    },
    enabled,
  });
}

export function useGetUserTodosSummary(
  orgId: string,
  deviceId?: string,
  enabled: boolean = false
) {
  return useQuery({
    queryKey: getUserTodosSummaryQueryKey(orgId, deviceId),
    queryFn: async () => {
      const { data } = await getUserTodosSummary(orgId, deviceId);
      return await UserTodosSummarySchema.validate(data);
    },
    enabled,
  });
}

export function useGetUserSurveyAnswers({ userId }: { userId: string }) {
  return useQuery({
    queryKey: getUserSurveyAnswersQueryKey(userId),
    queryFn: async () => {
      const { data } = await getUserSurveyAnswers();

      if (!data) {
        return null;
      }

      // this is because the user might not have any data yet and then data is an empty string
      return await UserSurveyAnswersSchema.validate(data);
    },
  });
}

export function useGetDocumentApproversQuery({
  documentId,
  versionId,
  orgId,
}: {
  documentId: string;
  versionId: string;
  orgId: string;
}) {
  return useQuery({
    queryKey: getApproversQueryKey(documentId, versionId),
    queryFn: async () => {
      const { data } = await getDocumentApprovers({
        orgId,
        documentId,
        versionId,
      });

      let approvers = data;

      if (!Array.isArray(data)) {
        captureSentryException("Invalid approvers data", {
          orgId,
          documentId,
          versionId,
          data,
        });
        approvers = [];
      }

      return await Promise.all(
        approvers.map(
          async (approver: any) =>
            await DocumentVersionApproverSchema.validateSync(approver)
        )
      );
    },
  });
}

export function useGetUsersByOrg({
  orgId,
  enabled = true,
}: {
  orgId: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: getOrganizationUsersQueryKey({
      orgId,
    }),
    queryFn: async () => {
      let users: OrgScopedBaseUser[] = [];
      let skip = 0;
      while (skip !== null) {
        const { data } = await getUsersByOrg({
          orgId,
          skip,
        });
        users.push(
          ...data.users.map((user: any) =>
            normalizeNullableUserNameFields(user)
          )
        );
        skip = data.nextSkip;
      }
      return await Promise.all(
        users.map(
          async (user: any) => await OrgScopedBaseUserSchema.validate(user)
        )
      );
    },
    enabled: enabled && !!orgId,
  });
}

export const getDevicesParams = ({
  orgId,
  enabled = true,
}: {
  orgId: string;
  enabled?: boolean;
}) => ({
  queryKey: getDevicesQueryKey(orgId),
  queryFn: async () => {
    const { data } = await getDevices(orgId);
    return Promise.all(
      data.map(async (device: Device) => {
        const validated = await deviceSchema.validate(device);
        return validated;
      })
    );
  },
  enabled: enabled && !!orgId,
});

export function useGetDevices({
  orgId,
  enabled = true,
}: {
  orgId: string;
  enabled?: boolean;
}) {
  return useQuery(getDevicesParams({ orgId, enabled }));
}

export const useGetFolders = ({
  orgId,
  enabled = true,
}: {
  orgId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: getFoldersQueryKey(orgId),
    queryFn: async () => {
      const { data } = await getFolders(orgId);
      return Promise.all(
        data.map(async (folder: Folder) => {
          const validated = await FolderSchema.validate(folder);
          return validated;
        })
      );
    },
    enabled: enabled && !!orgId,
  });
};

export type DataKey = {
  key: string;
  required: boolean;
  question: {
    id: string;
    type: string;
    options: { label: string; options: string[]; helperText: string };
  };
  context: string[];
  videoUrl: string | null;
  visibilityConditions: string | null;
  createdAt: Date;
  updatedAt: Date;
  dataKeyProtected: {
    agentGuidelines: string | null;
  };
};

export type DataKeys = DataKey[];

export function useGetDataKeys() {
  return useQuery({
    queryKey: ["data-keys"],
    queryFn: async () => {
      const { data } = await getDataKeys();
      return await data;
    },
  });
}

export function useGetDataKeyDefinitions(enabled: boolean = true) {
  return useQuery<DataKeys>({
    queryKey: getDataKeyDefinitionsQueryKey(),
    queryFn: async () => {
      const { data } = await getDataKeyDefinitions();
      return data;
    },
    enabled,
  });
}

export function useGetDevice({
  orgId,
  deviceId,
}: {
  orgId: string;
  deviceId: string;
}) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: getDeviceQueryKey(deviceId),
    queryFn: async () => {
      const { data } = await getDevice(orgId, deviceId);
      return await deviceSchema.validate(data);
    },
    initialData: () => {
      const devices: Device[] | undefined = queryClient.getQueryData([
        "devices",
      ]);
      return devices?.find((device: Device) => device.id === deviceId);
    },
    enabled: !!orgId && !!deviceId,
  });
}

export function useGetTraceabilityMatrix({
  orgId,
  deviceId,
}: {
  orgId: string;
  deviceId: string;
}) {
  return useQuery<TraceabilityMatrix>({
    queryKey: getTraceabilityMatrixQueryKey(orgId, deviceId),
    queryFn: async () => {
      const { data } = await getTraceabilityMatrix(orgId, deviceId);
      return await traceabilityMatrixSchema.validate(data);
    },
    enabled: !!orgId && !!deviceId,
  });
}

export function useGetDataKeyValue({
  orgId,
  deviceId,
  dataKey,
}: {
  orgId: string;
  deviceId: string;
  dataKey: string;
}) {
  return useQuery<string>({
    queryKey: getDataKeyValueQueryKey(orgId, deviceId, dataKey),
    queryFn: async () => {
      const { data } = await getDataKeyValue(orgId, deviceId, dataKey);
      return typeof data === "string" ? data : data?.value || "";
    },
    enabled: !!orgId && !!deviceId && !!dataKey,
  });
}

export function useGetAnswerItemsByType({
  orgId,
  type,
}: {
  orgId: string;
  type: string;
}) {
  return useQuery({
    queryKey: getAnswerItemsByTypeQueryKey({ orgId, type }),
    queryFn: async () => {
      const { data } = await getAnswerItemsByTypeId({
        orgId,
        type,
      });

      return await Promise.all(
        data.map(
          async (item: any) => await DocumentAnswerItemSchema.validate(item)
        )
      );
    },
    enabled: !!type,
  });
}

export function useGetAnswerItemById({
  orgId,
  itemId,
  type,
}: {
  orgId: string;
  itemId: string;
  type: string;
}) {
  return useQuery({
    queryKey: getAnswerItemsQueryKey({ orgId, itemId, type }),
    queryFn: async () => {
      const { data } = await getAnswerItemsById({
        orgId,
        itemId,
      });

      return await Promise.all(
        data.map(
          async (item: any) => await DocumentAnswerItemSchema.validate(item)
        )
      );
    },
    enabled: !!itemId,
  });
}

export const getDocumentsParams = (
  orgId: string,
  deviceId: string,
  options?: {
    explorer?: boolean;
    includeOrgDocs?: boolean;
    enabled?: boolean;
    queryKeyOrgId?: string;
    refetchInterval?: number | false;
  }
) => {
  return {
    queryKey: getDocumentsQueryKey(deviceId, {
      queryKeyOrgId: options?.queryKeyOrgId,
    }),
    queryFn: async () => {
      if (options?.explorer) {
        const documents = await getExplorerDocuments(orgId);

        return filterExplorerDocumentsForDevice({
          deviceId,
          documents,
          includeOrgDocs: options.includeOrgDocs ?? true,
        });
      }

      const { data } = await getDocuments(orgId, deviceId, {
        includeOrgDocs: options?.includeOrgDocs,
      });
      const docs: Document[] = await Promise.all(
        data.map(async (d: any) => DocumentSchema.validateSync(d))
      );

      return docs;
    },
    staleTime: 1000 * 60,
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
  };
};

const getExplorerDocuments = coalesceInFlightRequests(
  (orgId: string) => orgId,
  async (orgId: string) => {
    const { data } = await getDocuments(orgId, undefined, {
      view: "explorer",
    });

    return data.map((document: unknown) =>
      DocumentSchema.validateSync(document)
    );
  }
);

export function useGetDocuments(
  orgId: string,
  deviceId: string,
  options?: {
    includeOrgDocs?: boolean;
    enabled?: boolean;
    queryKeyOrgId?: string;
    refetchInterval?: number | false;
  }
) {
  return useQuery(getDocumentsParams(orgId, deviceId, options));
}

export function useGetTrashedDocuments(orgId: string) {
  return useQuery<TrashedDocument[]>({
    queryKey: getDocumentTrashQueryKey(orgId),
    queryFn: async () => {
      const { data } = await getDocuments(orgId, undefined, { view: "trash" });

      return data.map((document: unknown) =>
        TrashedDocumentSchema.validateSync(document)
      );
    },
    enabled: Boolean(orgId),
  });
}

export function useGetTrashedDocument(orgId: string, docId: string) {
  return useQuery<TrashedDocument>({
    queryKey: getTrashedDocumentQueryKey(orgId, docId),
    queryFn: async () => {
      const { data } = await getTrashedDocument(orgId, docId);

      return TrashedDocumentSchema.validateSync(data);
    },
    enabled: Boolean(orgId && docId),
    throwOnError: false,
  });
}

export function getDocumentsAsync(orgId: string, deviceId: string) {
  return queryClientAsync.fetchQuery(getDocumentsParams(orgId, deviceId));
}

const getTasksQueryParams = (orgId: string, deviceId: string) => {
  return {
    queryKey: getTasksQueryKey(orgId, deviceId),
    queryFn: () => deviceStore.fetchRoadmapTasks(orgId, deviceId),
    staleTime: 1000 * 60,
  };
};

// To allow passing data do options?: QueryOptions<RoadmapTasks[]> Passing the type of the data is important otherwise react-query will not know what type of data to expect
export function useGetTasks(orgId: string, deviceId: string) {
  return useQuery(getTasksQueryParams(orgId, deviceId));
}

export function getTasksAsync(orgId: string, deviceId: string) {
  return queryClientAsync.fetchQuery(getTasksQueryParams(orgId, deviceId));
}

/**
 * Resolves the document's dynamic content in a single call: the backend performs
 * the data-key two-tier resolution + freezing and returns the resolved `dataKeys`
 * map plus the always-live non-answer `context` map. The editor reports the exact
 * set of `dataKeys` it currently needs; the query key is keyed on that (sorted)
 * set so adding/removing a key refetches automatically, while
 * `placeholderData: keepPreviousData` keeps existing values visible across the
 * refetch (no flicker on already-resolved chips). Context is always returned in
 * full regardless of what's requested.
 */
export function useGetDynamicContent(
  orgId: string,
  docId: string,
  versionId: string | undefined,
  dataKeys: string[],
  enabled: boolean = true
) {
  // Sort + dedup for a stable cache key independent of registration order.
  const sortedDataKeys = Array.from(new Set(dataKeys)).sort();
  return useQuery<DynamicContentResolution>({
    queryKey: getDynamicContentForKeysQueryKey(
      docId,
      versionId,
      sortedDataKeys
    ),
    queryFn: async () => {
      const { data } = await getDynamicContent(orgId, docId, {
        versionId,
        dataKeys: sortedDataKeys,
      });
      return data && typeof data === "object"
        ? (data as DynamicContentResolution)
        : { dataKeys: {}, context: {} };
    },
    enabled: enabled && !!orgId && !!docId,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
  });
}

export const useGetDocumentWithAllVersions = (
  orgId: string,
  docId: string = "",
  options: { throwOnError?: boolean } = {}
) => {
  return useQuery({
    queryKey: getDocumentQueryKey(docId),
    queryFn: async () => {
      const { data, status } = await getDocument(orgId, docId);
      if (status !== 200) {
        return null;
      }
      return DocumentSchema.validate(data);
    },
    enabled: !!docId,
    ...options,
  });
};

export const useGetTrainingMatrixDocuments = (orgId: string, docIds: string[]) => {
  const documentIds = Array.from(new Set(docIds.filter(Boolean))).sort();
  const query = useQuery<Record<string, Document>>({
    queryKey: [
      ...getDocumentsQueryKey("", { queryKeyOrgId: orgId }),
      "training-matrix",
      documentIds,
    ],
    queryFn: async () => {
      const { data } = await getDocuments(orgId, undefined, {
        documentIds,
        allVersions: true,
      });
      const documents: Document[] = await Promise.all(
        data.map((document: unknown) => DocumentSchema.validate(document))
      );
      return Object.fromEntries(
        documents.map((document) => [document.id, document])
      );
    },
    enabled: Boolean(orgId) && documentIds.length > 0,
    retry: false,
    throwOnError: false,
    staleTime: 0,
  });
  return { ...query, data: query.data ?? {} };
};

export const useGetDocumentsWithAllVersions = (
  orgId: string,
  docIds: string[] = []
) => {
  const uniqueDocIds = Array.from(
    new Set(docIds.filter((docId): docId is string => Boolean(docId)))
  );

  const queries = useQueries({
    queries: uniqueDocIds.map((docId) => ({
      queryKey: getDocumentQueryKey(docId),
      queryFn: async () => {
        try {
          const { data, status } = await getDocument(orgId, docId);
          if (status !== 200) {
            const error = Object.assign(
              new Error(
                `Failed to load attachment document ${docId}: HTTP ${status}`
              ),
              { status }
            );
            throw error;
          }
          return await DocumentSchema.validate(data);
        } catch (error) {
          captureSentryException(error, {
            hook: "useGetDocumentsWithAllVersions",
            orgId,
            docId,
            status:
              error &&
              typeof error === "object" &&
              "status" in error &&
              typeof error.status === "number"
                ? error.status
                : undefined,
          });
          throw error;
        }
      },
      enabled: Boolean(orgId) && Boolean(docId),
      retry: false,
    })),
  });

  const data = uniqueDocIds.reduce<Record<string, Document>>(
    (acc, docId, index) => {
      const document = queries[index]?.data;
      if (document) {
        acc[docId] = document;
      }
      return acc;
    },
    {}
  );

  const failedDocIds = uniqueDocIds.filter(
    (_, index) => queries[index]?.isError
  );

  return {
    data,
    isLoading: queries.some((query) => query.isLoading),
    isFetching: queries.some((query) => query.isFetching),
    isError: queries.some((query) => query.isError),
    failedDocIds,
  };
};

export const useGetFile = ({
  orgId,
  fileId,
}: {
  orgId: string;
  fileId: string;
}) => {
  return useQuery({
    queryKey: getFileQueryKey(fileId),
    queryFn: async () => {
      const data = await getFile({ orgId, fileId });
      // return FileSchema.validate(data);
      return data;
    },
    enabled: !!fileId,
    staleTime: 1000 * 60 * 60 * 24, // 1 day
  });
};

export const useGetAllFilesInOrg = ({
  orgId,
  deviceId,
}: {
  orgId: string;
  deviceId?: string;
}) => {
  return useQuery({
    queryKey: getAllFilesInOrgQueryKey(deviceId),
    queryFn: async () => {
      const { data } = await getAllFilesInOrg({ orgId, deviceId });
      return data;
    },
  });
};

const templateStaleTime = 1000 * 15;

export const getDocumentsTemplatesParams = ({
  orgId,
  deviceId,
  enabled,
  explorer,
}: {
  orgId: string;
  deviceId: string;
  enabled?: boolean;
  explorer?: boolean;
}) => ({
  queryKey: getDocumentTemplatesQueryKey(orgId, deviceId),
  queryFn: async () => {
    if (explorer) {
      const templates = await getExplorerDocumentTemplates(orgId);
      const resolvedTemplates = resolveExplorerTemplatesForDevice({
        deviceId,
        orgId,
        templates,
      });

      return transformTemplatesConfigToFormat(resolvedTemplates);
    }

    const { data } = await getDocumentsTemplates(orgId, deviceId);
    const templates = await documentTemplateSchemas.validate(data);

    if (!templates) {
      throw new Error("No templates found");
    }

    return transformTemplatesConfigToFormat(templates);
  },
  enabled,
  staleTime: templateStaleTime,
});

const getExplorerDocumentTemplates = coalesceInFlightRequests(
  (orgId: string) => orgId,
  async (orgId: string) => {
    const { data } = await getDocumentExplorerTemplates(orgId);
    const templates = await documentTemplateSchemas.validate(data);

    if (!templates) {
      throw new Error("No templates found");
    }

    return templates.filter(
      (template): template is NonNullable<typeof template> =>
        template !== undefined
    );
  }
);

export const useGetDocumentsTemplates = ({
  orgId,
  deviceId,
  enabled,
}: {
  orgId: string;
  deviceId: string;
  enabled?: boolean;
}) => {
  return useQuery(getDocumentsTemplatesParams({ orgId, deviceId, enabled }));
};

/**
 * A frame template as served by the by-id endpoint: the template metadata with
 * `contentTemplate` replaced by `content`, the backend-serialized Tiptap JSON.
 */
export type FrameTemplate = Omit<DocumentTemplate, "contentTemplate"> & {
  content: JSONContent;
};

/**
 * The template a document instantiates, resolved by the backend with the same
 * device > org > product precedence. Hidden and deprecated templates still
 * resolve — they may not be listed in the Frames view, but they are this
 * document's template and the viewer can render them.
 */
export const useGetFrameTemplateForDocument = (
  orgId: string,
  document?: { templateKey: string | null; deviceId?: string | null } | null
) => {
  const templateKey = document?.templateKey ?? "";
  const deviceId = document?.deviceId ?? null;

  return useQuery({
    queryKey: getFrameTemplateForDocumentQueryKey(orgId, templateKey, deviceId),
    queryFn: async () => {
      const { data, status } = await getFrameTemplateByKey(
        orgId,
        templateKey,
        deviceId
      );

      if (status === 404) {
        return null;
      }

      return (data as DocumentTemplate) ?? null;
    },
    enabled: !!orgId && !!templateKey,
    staleTime: templateStaleTime,
  });
};

export const useGetFrameTemplate = (orgId: string, templateId: string) => {
  return useQuery({
    queryKey: getFrameTemplateQueryKey(orgId, templateId),
    queryFn: async () => {
      const { data } = await getFrameTemplateById(orgId, templateId);
      return data as FrameTemplate;
    },
    enabled: !!orgId && !!templateId,
  });
};

/**
 * Template-preview counterpart of `useGetDynamicContent`: resolves the
 * requested data keys from the latest owner answers in the org (optionally
 * scoped to a device) and returns the org/device context, with no document
 * involved. Keyed on the sorted requested set so registering a new key
 * refetches automatically; `keepPreviousData` avoids chip flicker.
 */
export function useGetTemplateDynamicContent(
  orgId: string,
  deviceId: string | undefined,
  dataKeys: string[],
  enabled: boolean = true
) {
  const sortedDataKeys = Array.from(new Set(dataKeys)).sort();
  return useQuery<DynamicContentResolution>({
    queryKey: getTemplateDynamicContentQueryKey(
      orgId,
      deviceId,
      sortedDataKeys
    ),
    queryFn: async () => {
      const { data } = await getTemplateDynamicContent(orgId, {
        ...(deviceId ? { deviceId } : {}),
        dataKeys: sortedDataKeys,
      });
      return data && typeof data === "object"
        ? (data as DynamicContentResolution)
        : { dataKeys: {}, context: {} };
    },
    enabled: enabled && !!orgId,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
  });
}

export const useGetRoadmapStages = ({
  orgId,
  deviceId,
  enabled,
}: {
  orgId: string;
  deviceId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: getRoadmapStagesQueryKey(orgId, deviceId),
    queryFn: async () => {
      const { data, status } = await getRoadmapStages(orgId, deviceId);
      if (status === 404) {
        return [];
      }

      return await roadmapStagesSchema.validate(data);
    },
    enabled,
    staleTime: templateStaleTime,
  });
};

export const useGetRoadmapCards = ({
  orgId,
  deviceId,
  enabled,
}: {
  orgId: string;
  deviceId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: getRoadmapCardsQueryKey(orgId, deviceId),
    queryFn: async () => {
      const { data, status } = await getRoadmapCards(orgId, deviceId);
      if (status === 404) {
        return [];
      }

      return await roadmapCardsSchema.validate(data);
    },
    enabled,
    staleTime: templateStaleTime,
  });
};

export const useGetRoadmapTasks = ({
  orgId,
  deviceId,
  enabled,
}: {
  orgId: string;
  deviceId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: getRoadmapTasksQueryKey(orgId, deviceId),
    queryFn: async () => {
      const { data, status } = await getRoadmapTasks(orgId, deviceId);
      if (status === 404) {
        return [];
      }

      return await taskDefinitionsSchema.validate(data);
    },
    enabled,
    staleTime: templateStaleTime,
  });
};

export const useGetAdminOrganizations = ({
  enabled = true,
}: {
  enabled?: boolean;
}) => {
  return useQuery<AdminOrganization[]>({
    queryKey: getAdminOrganizationsQueryKey(),
    queryFn: async () => {
      const { data } = await getAdminOrganizations();
      return data;
    },
    enabled,
  });
};

export const useGetAdminDevices = ({
  enabled = true,
}: {
  enabled?: boolean;
}) => {
  return useQuery<AdminDevice[]>({
    queryKey: getAdminDevicesQueryKey(),
    queryFn: async () => {
      const { data } = await getAdminDevices();
      return data;
    },
    enabled,
  });
};

export const useGetAdminRoadmapCards = ({
  enabled = true,
}: {
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: getAdminRoadmapCardsQueryKey(),
    queryFn: async () => {
      const { data } = await getAdminRoadmapCards();
      return roadmapCardsSchema.validate(data);
    },
    enabled,
  });
};

export const useGetAdminRoadmapStages = ({
  scope,
  orgId,
  deviceId,
  enabled = true,
}: {
  scope: "global" | "organization" | "device";
  orgId?: string;
  deviceId?: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: getAdminRoadmapStagesQueryKey({ scope, orgId, deviceId }),
    queryFn: async () => {
      const { data } = await getAdminRoadmapStages({ scope, orgId, deviceId });
      return roadmapStagesSchema.validate(data);
    },
    enabled,
  });
};

export const useGetIntegration = (orgId: string, type: IntegrationType) => {
  return useQuery({
    queryKey: getIntegrationQueryKey(orgId, type),
    queryFn: async () => {
      const { data } = await getIntegration(orgId, type);
      return await IntegrationResponseSchema.validate(data);
    },
  });
};

export const useGetSlackIntegration = (orgId: string) => {
  return useQuery({
    queryKey: getIntegrationQueryKey(orgId, IntegrationType.SLACK),
    queryFn: async () => {
      const { data } = await getIntegration(orgId, IntegrationType.SLACK);
      return SlackIntegrationResponseSchema.validate(data);
    },
  });
};

export const useGetMcpConnections = (
  orgId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: getMcpConnectionsQueryKey(orgId),
    queryFn: async () => {
      const { data } = await getMcpConnections(orgId);
      return await McpConnectionsResponseSchema.validate(data);
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    enabled: enabled && !!orgId,
  });
};

export const useGetSlackAuthUrl = (orgId: string) => {
  return useQuery({
    queryKey: getSlackAuthUrlQueryKey(orgId),
    queryFn: async () => {
      const { data } = await getSlackAuthUrl(orgId);
      return await SlackAuthUrlResponseSchema.validate(data);
    },
  });
};

export const useGetLinearAuthUrl = (orgId: string) => {
  return useQuery({
    queryKey: getLinearAuthUrlQueryKey(orgId),
    queryFn: async () => {
      const { data } = await getLinearAuthUrl(orgId);
      // Same response shape as Slack: { authUrl: string }
      return await SlackAuthUrlResponseSchema.validate(data);
    },
  });
};

export const useGetGithubAuthUrl = (orgId: string) => {
  return useQuery({
    queryKey: getGithubAuthUrlQueryKey(orgId),
    queryFn: async () => {
      const { data } = await getGithubAuthUrl(orgId);
      // Same response shape as Slack: { authUrl: string }
      return await SlackAuthUrlResponseSchema.validate(data);
    },
  });
};

export const useGetJiraAuthUrl = (orgId: string) => {
  return useQuery({
    queryKey: getJiraAuthUrlQueryKey(orgId),
    queryFn: async () => {
      const { data } = await getJiraAuthUrl(orgId);
      // Same response shape as Slack: { authUrl: string }
      return await SlackAuthUrlResponseSchema.validate(data);
    },
  });
};

export const useGetFunctionalGroups = (
  orgId: string,
  options?: { enabled?: boolean }
) => {
  const enabled = options?.enabled ?? true;
  return useQuery<FunctionalGroup[]>({
    queryKey: getFunctionalGroupsQueryKey(orgId),
    queryFn: async () => {
      const { data } = await getFunctionalGroups(orgId);
      return data;
    },
    enabled: enabled && !!orgId,
  });
};

// Task State exports
export * from "./queries/task-state";
export * from "./queries/task-state-activity";
export * from "./queries/task-state-mutations";

// Organization Tasks exports
export * from "./queries/org-tasks";

// Task Program (TRAINING) exports
export * from "./queries/task-program-mutations";
export * from "./queries/training-quiz";

// Dashboard exports
export * from "./queries/dashboard";

// Skills exports
export * from "./queries/skills";
